import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";

import {
    createPortal,
} from "react-dom";

import {
    FiAlertCircle,
    FiArrowDown,
    FiArrowUp,
    FiBox,
    FiCopy,
    FiMenu,
    FiMinus,
    FiPlus,
    FiSearch,
    FiTrash2,
    FiX,
} from "react-icons/fi";

import API from "../../../../../../../utils/apiCalling";
import { config } from "../../../../../../../utils/apiUrl";

import "./InvoiceItemsTable.css";

/*
|--------------------------------------------------------------------------
| CONSTANTS
|--------------------------------------------------------------------------
*/

const api = new API();

const UNIT_OPTIONS = [
    "piece",
    "pair",
    "box",
    "packet",
    "kg",
    "gram",
    "liter",
    "meter",
    "set",
    "service",
    "hour",
    "day",
    "month",
    "none",
];

const TAX_OPTIONS = [
    0,
    0.1,
    0.25,
    1,
    3,
    5,
    12,
    18,
    28,
];

const PRICE_MODE_OPTIONS = [
    {
        value: "TAX_EXCLUSIVE",
        label: "Exclusive",
    },
    {
        value: "TAX_INCLUSIVE",
        label: "Inclusive",
    },
];

const DISCOUNT_TYPE_OPTIONS = [
    {
        value: "NONE",
        label: "None",
    },
    {
        value: "PERCENTAGE",
        label: "%",
    },
    {
        value: "FLAT",
        label: "₹",
    },
];

/*
|--------------------------------------------------------------------------
| HELPERS
|--------------------------------------------------------------------------
*/

const normalizeText = (
    value
) => String(
    value ?? ""
).trim();

const toNumber = (
    value,
    fallback = 0
) => {
    const parsed =
        Number(value);

    return Number.isFinite(
        parsed
    )
        ? parsed
        : fallback;
};

const roundMoney = (
    value
) =>
    Math.round(
        (
            toNumber(value) +
            Number.EPSILON
        ) *
            100
    ) / 100;

const createTemporaryId = () =>
    `invoice-item-${Date.now()}-${Math.random()
        .toString(36)
        .slice(2, 10)}`;

const createEmptyInvoiceItem = (
    defaults = {}
) => ({
    temporaryId:
        createTemporaryId(),

    productId: "",
    itemType: "MANUAL",

    itemName: "",
    description: "",

    sku: "",
    barcode: "",

    brand: "",
    modelNumber: "",

    hsnSacCode: "",

    quantity: 1,
    unit:
        defaults.defaultUnit ||
        "piece",

    rate: 0,

    priceMode:
        defaults.defaultPriceMode ||
        "TAX_EXCLUSIVE",

    discountType: "NONE",
    discountValue: 0,

    taxPercentage:
        toNumber(
            defaults.defaultTaxPercentage,
            0
        ),

    cessPercentage: 0,

    availableStock: 0,
    reservedStock: 0,

    warranty: "",
    warrantyMonths: 0,

    metadata: {},
});

const normalizeInvoiceItem = (
    item,
    defaults = {}
) => ({
    ...createEmptyInvoiceItem(
        defaults
    ),
    ...(item || {}),

    temporaryId:
        item?.temporaryId ||
        item?._id ||
        createTemporaryId(),

    productId:
        item?.productId ||
        item?.product?._id ||
        item?.product ||
        "",

    itemType:
        item?.itemType ||
        (
            item?.productId ||
            item?.product
                ? "INVENTORY"
                : "MANUAL"
        ),

    itemName:
        item?.itemName ||
        item?.name ||
        item?.productName ||
        "",

    quantity:
        Math.max(
            toNumber(
                item?.quantity,
                1
            ),
            0
        ),

    rate:
        Math.max(
            toNumber(
                item?.rate ??
                item?.unitPrice ??
                0
            ),
            0
        ),

    discountValue:
        Math.max(
            toNumber(
                item?.discountValue,
                0
            ),
            0
        ),

    taxPercentage:
        Math.max(
            toNumber(
                item?.taxPercentage ??
                defaults.defaultTaxPercentage ??
                0
            ),
            0
        ),

    cessPercentage:
        Math.max(
            toNumber(
                item?.cessPercentage,
                0
            ),
            0
        ),

    availableStock:
        Math.max(
            toNumber(
                item?.availableStock ??
                item?.metadata?.availableStock ??
                0
            ),
            0
        ),

    reservedStock:
        Math.max(
            toNumber(
                item?.reservedStock ??
                item?.reservedQty ??
                0
            ),
            0
        ),

    metadata: {
        ...(item?.metadata ||
            {}),
    },
});

const calculateInvoiceItem = (
    item,
    isInterState = false
) => {
    const quantity =
        Math.max(
            toNumber(
                item.quantity
            ),
            0
        );

    const rate =
        Math.max(
            toNumber(
                item.rate
            ),
            0
        );

    const taxPercentage =
        Math.max(
            toNumber(
                item.taxPercentage
            ),
            0
        );

    const cessPercentage =
        Math.max(
            toNumber(
                item.cessPercentage
            ),
            0
        );

    const grossAmount =
        roundMoney(
            quantity *
                rate
        );

    let taxableBeforeDiscount =
        grossAmount;

    if (
        item.priceMode ===
        "TAX_INCLUSIVE"
    ) {
        const totalTaxRate =
            taxPercentage +
            cessPercentage;

        taxableBeforeDiscount =
            totalTaxRate >
            0
                ? roundMoney(
                    grossAmount /
                        (
                            1 +
                            totalTaxRate /
                                100
                        )
                )
                : grossAmount;
    }

    let discountAmount = 0;

    if (
        item.discountType ===
        "PERCENTAGE"
    ) {
        discountAmount =
            roundMoney(
                taxableBeforeDiscount *
                    Math.min(
                        Math.max(
                            toNumber(
                                item.discountValue
                            ),
                            0
                        ),
                        100
                    ) /
                    100
            );
    } else if (
        item.discountType ===
        "FLAT"
    ) {
        discountAmount =
            Math.min(
                roundMoney(
                    Math.max(
                        toNumber(
                            item.discountValue
                        ),
                        0
                    )
                ),
                taxableBeforeDiscount
            );
    }

    const taxableAmount =
        roundMoney(
            Math.max(
                taxableBeforeDiscount -
                    discountAmount,
                0
            )
        );

    const taxAmount =
        roundMoney(
            taxableAmount *
                taxPercentage /
                100
        );

    const cessAmount =
        roundMoney(
            taxableAmount *
                cessPercentage /
                100
        );

    const igstAmount =
        isInterState
            ? taxAmount
            : 0;

    const cgstAmount =
        isInterState
            ? 0
            : roundMoney(
                taxAmount /
                    2
            );

    const sgstAmount =
        isInterState
            ? 0
            : roundMoney(
                taxAmount -
                    cgstAmount
            );

    const totalAmount =
        roundMoney(
            taxableAmount +
                taxAmount +
                cessAmount
        );

    return {
        grossAmount,
        discountAmount,
        taxableAmount,
        taxAmount,
        cessAmount,
        cgstAmount,
        sgstAmount,
        igstAmount,
        totalAmount,
    };
};

const validateInvoiceItem = (
    item,
    {
        allowZeroStock = false,
        allowOverSelling = false,
    } = {}
) => {
    const errors = {};

    if (
        !normalizeText(
            item.itemName
        )
    ) {
        errors.itemName =
            "Item name is required.";
    }

    if (
        toNumber(
            item.quantity
        ) <= 0
    ) {
        errors.quantity =
            "Quantity must be greater than zero.";
    }

    if (
        toNumber(
            item.rate
        ) < 0
    ) {
        errors.rate =
            "Rate cannot be negative.";
    }

    if (
        item.itemType ===
            "INVENTORY" &&
        !item.productId
    ) {
        errors.productId =
            "Select a valid inventory product.";
    }

    if (
        item.itemType ===
            "INVENTORY" &&
        !allowZeroStock &&
        toNumber(
            item.availableStock
        ) <= 0
    ) {
        errors.stock =
            "Product is out of stock.";
    }

    if (
        item.itemType ===
            "INVENTORY" &&
        !allowOverSelling &&
        toNumber(
            item.quantity
        ) >
            toNumber(
                item.availableStock
            )
    ) {
        errors.quantity =
            `Only ${toNumber(
                item.availableStock
            )} unit(s) available.`;
    }

    if (
        item.discountType ===
            "PERCENTAGE" &&
        (
            toNumber(
                item.discountValue
            ) <
                0 ||
            toNumber(
                item.discountValue
            ) >
                100
        )
    ) {
        errors.discountValue =
            "Discount must be between 0 and 100.";
    }

    return errors;
};

const getProductId = (
    product
) =>
    product?._id ||
    product?.id ||
    "";

const getProductName = (
    product
) =>
    product?.productName ||
    product?.name ||
    product?.title ||
    "";

const getProductSku = (
    product
) =>
    product?.sku ||
    product?.productCode ||
    product?.code ||
    "";

const getAvailableStock = (
    product
) => {
    const stockQty =
        toNumber(
            product?.stockQty ??
            product?.availableStock ??
            product?.stock ??
            0
        );

    const reservedQty =
        toNumber(
            product?.reservedQty ??
            product?.reservedStock ??
            0
        );

    return Math.max(
        stockQty -
            reservedQty,
        0
    );
};


const getProductImage = (
    product
) => {

    const rawImage =
        product?.thumbnail ||
        product?.image ||
        (
            Array.isArray(
                product?.images
            )
                ? product.images[0]
                : ""
        ) ||
        "";


    if (
        !rawImage
    ) {
        return "";
    }


    const normalized =
        String(
            rawImage
        )
            .trim()
            .replace(
                /\\/g,
                "/"
            );


    if (
        /^(https?:|data:|blob:)/i.test(
            normalized
        )
    ) {
        return normalized;
    }


    /*
    |--------------------------------------------------------------------------
    | BUILD ABSOLUTE URL FROM BILLING API ENDPOINT
    |--------------------------------------------------------------------------
    |
    | Example:
    | http://localhost:9003/v1/admin/billing/products/search
    | -> http://localhost:9003/uploads/...
    |
    |--------------------------------------------------------------------------
    */

    const apiBase =
        String(
            config
                ?.searchBillingProducts ||
            ""
        )
            .split(
                "/v1/"
            )[0]
            .replace(
                /\/+$/,
                ""
            );


    if (
        !apiBase
    ) {
        return normalized;
    }


    return `${apiBase}/${normalized.replace(
        /^\/+/,
        ""
    )}`;

};


const getResponseProducts = (
    response
) => {
    const candidates = [
        response?.data?.products,
        response?.data?.data?.products,
        response?.products,
        response?.result?.products,
        response?.data?.records,
        response?.records,
        response?.data?.items,
        response?.items,
    ];

    return (
        candidates.find(
            Array.isArray
        ) ||
        []
    );
};

const mapProductToInvoiceItem = (
    product,
    defaults = {}
) => {
    const availableStock =
        getAvailableStock(
            product
        );

    return {
        ...createEmptyInvoiceItem(
            defaults
        ),

        productId:
            getProductId(
                product
            ),

        itemType:
            product
                ?.productType ===
            "service"
                ? "SERVICE"
                : "INVENTORY",

        itemName:
            getProductName(
                product
            ),

        description:
            product
                ?.shortDescription ||
            product?.description ||
            "",

        sku:
            getProductSku(
                product
            ),

        barcode:
            product?.barcode ||
            "",

        brand:
            product?.brand?.name ||
            product?.brand ||
            "",

        modelNumber:
            product?.modelNumber ||
            "",

        hsnSacCode:
            product?.hsnSacCode ||
            product?.hsnCode ||
            "",

        unit:
            product?.unit ||
            defaults.defaultUnit ||
            "piece",

        quantity: 1,

        rate:
            roundMoney(
                product
                    ?.discountedPrice >
                    0
                    ? product
                        .discountedPrice
                    : product
                        ?.sellingPrice ||
                        0
            ),

        priceMode:
            product
                ?.priceIncludesTax ===
            true
                ? "TAX_INCLUSIVE"
                : defaults.defaultPriceMode ||
                "TAX_EXCLUSIVE",

        taxPercentage:
            toNumber(
                product
                    ?.taxPercentage ??
                defaults.defaultTaxPercentage ??
                0
            ),

        cessPercentage:
            toNumber(
                product?.cessPercentage ??
                0
            ),

        availableStock,

        reservedStock:
            toNumber(
                product?.reservedQty ??
                0
            ),

        warranty:
            product?.warranty ||
            "",

        warrantyMonths:
            toNumber(
                product?.warrantyMonths ??
                0
            ),

        metadata: {
            availableStock,

            thumbnail:
                getProductImage(
                    product
                ),

            purchasePriceSnapshot:
                toNumber(
                    product?.purchasePrice ??
                    0
                ),

            condition:
                product?.condition ||
                "",

            productType:
                product?.productType ||
                "physical",
        },
    };
};

/*
|--------------------------------------------------------------------------
| PRODUCT CELL
|--------------------------------------------------------------------------
*/

const ProductAutocompleteCell = ({
    item,
    rowIndex,
    disabled,
    defaults,
    allowManual,
    onSelect,
    onManual,
}) => {
    const rootRef =
        useRef(null);

    const dropdownRef =
        useRef(null);

    const requestRef =
        useRef(0);

    const [
        searchText,
        setSearchText,
    ] = useState(
        item.itemName ||
        ""
    );

    const [
        results,
        setResults,
    ] = useState([]);

    const [
        loading,
        setLoading,
    ] = useState(false);

    const [
        error,
        setError,
    ] = useState("");

    const [
        open,
        setOpen,
    ] = useState(false);

    const [
        activeIndex,
        setActiveIndex,
    ] = useState(-1);


    const [
        dropdownStyle,
        setDropdownStyle,
    ] =
        useState({
            top:
                0,

            left:
                0,

            width:
                480,

            maxHeight:
                320,
        });

    useEffect(() => {
        setSearchText(
            item.itemName ||
            ""
        );
    }, [
        item.itemName,
        item.productId,
    ]);

    useEffect(() => {
        const handleOutsideClick = (
            event
        ) => {
            const clickedInsideInput =
                rootRef.current &&
                rootRef.current.contains(
                    event.target
                );


            const clickedInsideDropdown =
                dropdownRef.current &&
                dropdownRef.current.contains(
                    event.target
                );


            if (
                !clickedInsideInput &&
                !clickedInsideDropdown
            ) {
                setOpen(false);
                setActiveIndex(-1);
            }
        };

        document.addEventListener(
            "mousedown",
            handleOutsideClick
        );

        return () => {
            document.removeEventListener(
                "mousedown",
                handleOutsideClick
            );
        };
    }, []);

    const updateDropdownPosition =
        useCallback(
            () => {

                const root =
                    rootRef.current;


                if (
                    !root
                ) {
                    return;
                }


                const rect =
                    root.getBoundingClientRect();


                const viewportWidth =
                    window.innerWidth ||
                    document.documentElement
                        .clientWidth ||
                    0;


                const viewportHeight =
                    window.innerHeight ||
                    document.documentElement
                        .clientHeight ||
                    0;


                const horizontalGap =
                    12;


                const preferredWidth =
                    Math.min(
                        520,
                        Math.max(
                            360,
                            viewportWidth -
                            horizontalGap *
                            2
                        )
                    );


                const width =
                    Math.min(
                        preferredWidth,
                        viewportWidth -
                        horizontalGap *
                        2
                    );


                let left =
                    rect.left;


                if (
                    left +
                    width >
                    viewportWidth -
                    horizontalGap
                ) {
                    left =
                        viewportWidth -
                        horizontalGap -
                        width;
                }


                left =
                    Math.max(
                        horizontalGap,
                        left
                    );


                const spaceBelow =
                    viewportHeight -
                    rect.bottom -
                    12;


                const spaceAbove =
                    rect.top -
                    12;


                const openUpward =
                    spaceBelow <
                        220 &&
                    spaceAbove >
                        spaceBelow;


                const availableHeight =
                    Math.max(
                        140,
                        openUpward
                            ? spaceAbove
                            : spaceBelow
                    );


                const maxHeight =
                    Math.min(
                        340,
                        availableHeight
                    );


                const top =
                    openUpward
                        ? Math.max(
                            8,
                            rect.top -
                            maxHeight -
                            6
                        )
                        : rect.bottom +
                            6;


                setDropdownStyle({
                    top,

                    left,

                    width,

                    maxHeight,
                });

            },
            []
        );


    useEffect(
        () => {

            if (
                !open
            ) {
                return undefined;
            }


            updateDropdownPosition();


            const handleReposition =
                () =>
                    updateDropdownPosition();


            window.addEventListener(
                "resize",
                handleReposition
            );


            window.addEventListener(
                "scroll",
                handleReposition,
                true
            );


            return () => {

                window.removeEventListener(
                    "resize",
                    handleReposition
                );


                window.removeEventListener(
                    "scroll",
                    handleReposition,
                    true
                );

            };

        },
        [
            open,
            updateDropdownPosition,
        ]
    );


    useEffect(() => {
        const keyword =
            normalizeText(
                searchText
            );

        if (
            item.productId &&
            keyword ===
                item.itemName
        ) {
            setResults([]);
            setError("");
            return undefined;
        }

        if (
            keyword.length <
            2
        ) {
            setResults([]);
            setLoading(false);
            setError("");
            return undefined;
        }

        const timer =
            window.setTimeout(
                async () => {
                    const requestId =
                        requestRef.current +
                        1;

                    requestRef.current =
                        requestId;

                    try {
                        setLoading(true);
                        setError("");

                        /*
                        |--------------------------------------------------------------------------
                        | EXPLICIT PRODUCT SEARCH QUERY
                        |--------------------------------------------------------------------------
                        |
                        | Do not depend on the generic GET helper to serialize params.
                        | The customer search had the same runtime issue.
                        |
                        |--------------------------------------------------------------------------
                        */

                        const queryParams =
                            new URLSearchParams({
                                search:
                                    keyword,

                                page:
                                    "1",

                                limit:
                                    "12",
                            });


                        const response =
                            await api.get(
                                `${config.searchBillingProducts}?${queryParams.toString()}`
                            );

                        if (
                            requestId !==
                            requestRef.current
                        ) {
                            return;
                        }

                        const products =
                            getResponseProducts(
                                response
                            );

                        setResults(
                            products
                        );

                        setOpen(
                            products.length >
                            0
                        );

                        setActiveIndex(
                            products.length
                                ? 0
                                : -1
                        );
                    } catch (
                        requestError
                    ) {
                        if (
                            requestId !==
                            requestRef.current
                        ) {
                            return;
                        }

                        setResults([]);
                        setError(
                            requestError
                                ?.response
                                ?.data
                                ?.message ||
                            requestError
                                ?.message ||
                            "Unable to search products."
                        );
                        setOpen(false);
                    } finally {
                        if (
                            requestId ===
                            requestRef.current
                        ) {
                            setLoading(false);
                        }
                    }
                },
                300
            );

        return () => {
            window.clearTimeout(
                timer
            );
        };
    }, [
        item.itemName,
        item.productId,
        searchText,
    ]);

    const handleKeyDown =
        useCallback(
            (
                event
            ) => {
                if (
                    !open
                ) {
                    if (
                        event.key ===
                        "ArrowDown"
                    ) {
                        setOpen(true);
                    }

                    return;
                }

                if (
                    event.key ===
                    "ArrowDown"
                ) {
                    event.preventDefault();

                    setActiveIndex(
                        (
                            previous
                        ) =>
                            Math.min(
                                previous +
                                    1,
                                results.length -
                                    1
                            )
                    );

                    return;
                }

                if (
                    event.key ===
                    "ArrowUp"
                ) {
                    event.preventDefault();

                    setActiveIndex(
                        (
                            previous
                        ) =>
                            Math.max(
                                previous -
                                    1,
                                0
                            )
                    );

                    return;
                }

                if (
                    event.key ===
                    "Enter"
                ) {
                    event.preventDefault();

                    if (
                        activeIndex >=
                            0 &&
                        results[
                            activeIndex
                        ]
                    ) {
                        const selected =
                            mapProductToInvoiceItem(
                                results[
                                    activeIndex
                                ],
                                defaults
                            );

                        onSelect(
                            rowIndex,
                            selected
                        );

                        setOpen(false);
                        setResults([]);
                        return;
                    }

                    if (
                        allowManual &&
                        normalizeText(
                            searchText
                        )
                    ) {
                        onManual(
                            rowIndex,
                            searchText
                        );

                        setOpen(false);
                    }

                    return;
                }

                if (
                    event.key ===
                    "Escape"
                ) {
                    setOpen(false);
                    setActiveIndex(-1);
                }
            },
            [
                activeIndex,
                allowManual,
                defaults,
                onManual,
                onSelect,
                open,
                results,
                rowIndex,
                searchText,
            ]
        );

    return (
        <div
            ref={rootRef}
            className="invoice-items-product-cell"
        >
            <div className="invoice-items-product-input">
                <FiSearch />

                <input
                    type="text"
                    value={
                        searchText
                    }
                    disabled={
                        disabled
                    }
                    placeholder="Search product or type manual item"
                    autoComplete="off"
                    onFocus={() => {
                        if (
                            results.length >
                            0
                        ) {
                            setOpen(true);
                        }
                    }}
                    onChange={(
                        event
                    ) => {
                        const nextValue =
                            event.target
                                .value;

                        setSearchText(
                            nextValue
                        );

                        if (
                            item.productId
                        ) {
                            onManual(
                                rowIndex,
                                nextValue
                            );
                        } else {
                            onManual(
                                rowIndex,
                                nextValue,
                                false
                            );
                        }
                    }}
                    onKeyDown={
                        handleKeyDown
                    }

                    onBlur={() => {

                        /*
                        |--------------------------------------------------------------------------
                        | CLOSE AUTOCOMPLETE WHEN FOCUS MOVES AWAY
                        |--------------------------------------------------------------------------
                        |
                        | Example:
                        | Product input -> press TAB -> Description input
                        |
                        | The dropdown must close immediately.
                        |
                        | setTimeout keeps mouse selection inside the portaled
                        | dropdown working correctly.
                        |
                        |--------------------------------------------------------------------------
                        */

                        window.setTimeout(
                            () => {

                                const activeElement =
                                    document.activeElement;


                                const focusInsideInput =
                                    rootRef.current &&
                                    rootRef.current.contains(
                                        activeElement
                                    );


                                const focusInsideDropdown =
                                    dropdownRef.current &&
                                    dropdownRef.current.contains(
                                        activeElement
                                    );


                                if (
                                    !focusInsideInput &&
                                    !focusInsideDropdown
                                ) {

                                    setOpen(
                                        false
                                    );

                                    setActiveIndex(
                                        -1
                                    );

                                }

                            },
                            0
                        );

                    }}
                />

                {loading && (
                    <span className="invoice-items-product-spinner" />
                )}

                {/* {!loading &&
                    searchText && (
                        <button
                            type="button"
                            className="invoice-items-product-clear"
                            disabled={
                                disabled
                            }
                            onClick={() => {
                                setSearchText(
                                    ""
                                );

                                onManual(
                                    rowIndex,
                                    ""
                                );
                            }}
                        >
                            <FiX />
                        </button>
                    )} */}
            </div>

            <div className="invoice-items-product-subline">
                {item.sku && (
                    <span>
                        SKU: {item.sku}
                    </span>
                )}

                {item.itemType ===
                    "INVENTORY" && (
                    <span
                        className={
                            item.availableStock >
                            0
                                ? "invoice-items-stock-available"
                                : "invoice-items-stock-out"
                        }
                    >
                        {item.availableStock >
                        0
                            ? `${item.availableStock} in stock`
                            : "Out of stock"}
                    </span>
                )}

                {item.itemType !==
                    "INVENTORY" && (
                        ""
                    // <span className="invoice-items-manual-badge">
                    //     Manual
                    // </span>
                )}
            </div>

            {open &&
                typeof document !==
                    "undefined" &&
                createPortal(
                    <div
                        ref={
                            dropdownRef
                        }
                        className="invoice-items-product-dropdown invoice-items-product-dropdown-portal"
                        style={{
                            top:
                                `${dropdownStyle.top}px`,

                            left:
                                `${dropdownStyle.left}px`,

                            width:
                                `${dropdownStyle.width}px`,

                            maxHeight:
                                `${dropdownStyle.maxHeight}px`,
                        }}
                    >
                    {loading && (
                        <div className="invoice-items-product-state">
                            Searching products...
                        </div>
                    )}

                    {!loading &&
                        error && (
                            <div className="invoice-items-product-state invoice-items-product-error">
                                <FiAlertCircle />

                                {error}
                            </div>
                        )}

                    {!loading &&
                        !error &&
                        results.map(
                            (
                                product,
                                index
                            ) => {
                                const id =
                                    getProductId(
                                        product
                                    );

                                const name =
                                    getProductName(
                                        product
                                    );

                                const sku =
                                    getProductSku(
                                        product
                                    );

                                const stock =
                                    getAvailableStock(
                                        product
                                    );

                                const rate =
                                    roundMoney(
                                        product
                                            ?.discountedPrice >
                                            0
                                            ? product
                                                .discountedPrice
                                            : product
                                                ?.sellingPrice ||
                                                0
                                    );

                                return (
                                    <button
                                        key={
                                            id ||
                                            `${name}-${index}`
                                        }
                                        type="button"
                                        className={
                                            index ===
                                            activeIndex
                                                ? "invoice-items-product-result invoice-items-product-result-active"
                                                : "invoice-items-product-result"
                                        }
                                        onMouseEnter={() =>
                                            setActiveIndex(
                                                index
                                            )
                                        }
                                        onClick={() => {
                                            onSelect(
                                                rowIndex,
                                                mapProductToInvoiceItem(
                                                    product,
                                                    defaults
                                                )
                                            );

                                            setOpen(
                                                false
                                            );

                                            setResults(
                                                []
                                            );
                                        }}
                                    >
                                        <span className="invoice-items-product-result-icon">
                                            {getProductImage(
                                                product
                                            ) ? (
                                                <img
                                                    src={
                                                        getProductImage(
                                                            product
                                                        )
                                                    }
                                                    alt={
                                                        name
                                                    }
                                                />
                                            ) : (
                                                <FiBox />
                                            )}
                                        </span>

                                        <span className="invoice-items-product-result-content">
                                            <strong>
                                                {name ||
                                                    "Unnamed Product"}
                                            </strong>

                                            <small>
                                                {[
                                                    sku
                                                        ? `SKU: ${sku}`
                                                        : "",
                                                    product
                                                        ?.brand,
                                                    product
                                                        ?.modelNumber,
                                                ]
                                                    .filter(
                                                        Boolean
                                                    )
                                                    .join(
                                                        " • "
                                                    ) ||
                                                    "No additional details"}
                                            </small>
                                        </span>

                                        <span className="invoice-items-product-result-meta">
                                            <strong>
                                                ₹
                                                {rate.toLocaleString(
                                                    "en-IN",
                                                    {
                                                        minimumFractionDigits:
                                                            2,
                                                        maximumFractionDigits:
                                                            2,
                                                    }
                                                )}
                                            </strong>

                                            <small
                                                className={
                                                    stock >
                                                    0
                                                        ? "invoice-items-stock-available"
                                                        : "invoice-items-stock-out"
                                                }
                                            >
                                                {stock >
                                                0
                                                    ? `${stock} in stock`
                                                    : "Out of stock"}
                                            </small>
                                        </span>
                                    </button>
                                );
                            }
                        )}

                    </div>,
                    document.body
                )}
        </div>
    );
};

/*
|--------------------------------------------------------------------------
| COMPONENT
|--------------------------------------------------------------------------
*/

const InvoiceItemsTable = ({
    items = [],
    onChange,
    onValidationChange,
    defaults = {},
    disabled = false,
    allowManual = true,
    allowZeroStock = false,
    allowOverSelling = false,
    isInterState = false,
    minimumRows = 1,
    showDescription = true,
    showCess = false,
    showPriceMode = true,
    showStock = true,
}) => {
    const normalizedItems =
        useMemo(
            () => {
                const source =
                    Array.isArray(
                        items
                    )
                        ? items
                        : [];

                const normalized =
                    source.map(
                        (
                            item
                        ) =>
                            normalizeInvoiceItem(
                                item,
                                defaults
                            )
                    );

                while (
                    normalized.length <
                    minimumRows
                ) {
                    normalized.push(
                        createEmptyInvoiceItem(
                            defaults
                        )
                    );
                }

                return normalized;
            },
            [
                defaults,
                items,
                minimumRows,
            ]
        );

    const [
        touchedRows,
        setTouchedRows,
    ] = useState({});

    const [
        draggedRowId,
        setDraggedRowId,
    ] = useState("");

    const [
        dragOverRowId,
        setDragOverRowId,
    ] = useState("");

    const rowErrors =
        useMemo(
            () =>
                normalizedItems.map(
                    (
                        item
                    ) =>
                        validateInvoiceItem(
                            item,
                            {
                                allowZeroStock,
                                allowOverSelling,
                            }
                        )
                ),
            [
                allowOverSelling,
                allowZeroStock,
                normalizedItems,
            ]
        );

    const calculatedItems =
        useMemo(
            () =>
                normalizedItems.map(
                    (
                        item
                    ) => ({
                        ...item,
                        calculation:
                            calculateInvoiceItem(
                                item,
                                isInterState
                            ),
                    })
                ),
            [
                isInterState,
                normalizedItems,
            ]
        );

    const totals =
        useMemo(
            () =>
                calculatedItems.reduce(
                    (
                        accumulator,
                        item
                    ) => ({
                        grossAmount:
                            roundMoney(
                                accumulator
                                    .grossAmount +
                                item
                                    .calculation
                                    .grossAmount
                            ),

                        discountAmount:
                            roundMoney(
                                accumulator
                                    .discountAmount +
                                item
                                    .calculation
                                    .discountAmount
                            ),

                        taxableAmount:
                            roundMoney(
                                accumulator
                                    .taxableAmount +
                                item
                                    .calculation
                                    .taxableAmount
                            ),

                        cgstAmount:
                            roundMoney(
                                accumulator
                                    .cgstAmount +
                                item
                                    .calculation
                                    .cgstAmount
                            ),

                        sgstAmount:
                            roundMoney(
                                accumulator
                                    .sgstAmount +
                                item
                                    .calculation
                                    .sgstAmount
                            ),

                        igstAmount:
                            roundMoney(
                                accumulator
                                    .igstAmount +
                                item
                                    .calculation
                                    .igstAmount
                            ),

                        cessAmount:
                            roundMoney(
                                accumulator
                                    .cessAmount +
                                item
                                    .calculation
                                    .cessAmount
                            ),

                        totalAmount:
                            roundMoney(
                                accumulator
                                    .totalAmount +
                                item
                                    .calculation
                                    .totalAmount
                            ),
                    }),
                    {
                        grossAmount: 0,
                        discountAmount: 0,
                        taxableAmount: 0,
                        cgstAmount: 0,
                        sgstAmount: 0,
                        igstAmount: 0,
                        cessAmount: 0,
                        totalAmount: 0,
                    }
                ),
            [calculatedItems]
        );

    useEffect(() => {
        if (
            typeof onValidationChange ===
            "function"
        ) {
            onValidationChange({
                valid:
                    rowErrors.every(
                        (
                            errors
                        ) =>
                            Object.keys(
                                errors
                            ).length ===
                            0
                    ),

                rowErrors,

                totals,
            });
        }
    }, [
        onValidationChange,
        rowErrors,
        totals,
    ]);

    const emitItems =
        useCallback(
            (
                nextItems
            ) => {
                onChange?.(
                    nextItems
                );
            },
            [onChange]
        );

    const updateItem =
        useCallback(
            (
                rowIndex,
                patch
            ) => {
                const nextItems =
                    normalizedItems.map(
                        (
                            item,
                            index
                        ) =>
                            index ===
                            rowIndex
                                ? {
                                    ...item,
                                    ...patch,

                                    metadata: {
                                        ...item.metadata,
                                        ...(
                                            patch.metadata ||
                                            {}
                                        ),
                                    },
                                }
                                : item
                    );

                emitItems(
                    nextItems
                );
            },
            [
                emitItems,
                normalizedItems,
            ]
        );

    const markTouched =
        useCallback(
            (
                rowIndex,
                field
            ) => {
                setTouchedRows(
                    (
                        previous
                    ) => ({
                        ...previous,

                        [rowIndex]: {
                            ...(
                                previous[
                                    rowIndex
                                ] ||
                                {}
                            ),

                            [field]:
                                true,
                        },
                    })
                );
            },
            []
        );

    const getVisibleError =
        useCallback(
            (
                rowIndex,
                field
            ) => {
                return touchedRows[
                    rowIndex
                ]?.[field]
                    ? rowErrors[
                        rowIndex
                    ]?.[field]
                    : "";
            },
            [
                rowErrors,
                touchedRows,
            ]
        );

    const handleSelectProduct =
        useCallback(
            (
                rowIndex,
                selectedItem
            ) => {
                updateItem(
                    rowIndex,
                    {
                        ...selectedItem,

                        temporaryId:
                            normalizedItems[
                                rowIndex
                            ]
                                ?.temporaryId ||
                            createTemporaryId(),
                    }
                );
            },
            [
                normalizedItems,
                updateItem,
            ]
        );

    const handleManualItem =
        useCallback(
            (
                rowIndex,
                itemName,
                resetInventory = true
            ) => {
                const current =
                    normalizedItems[
                        rowIndex
                    ];

                updateItem(
                    rowIndex,
                    {
                        ...(resetInventory
                            ? {
                                productId:
                                    "",

                                itemType:
                                    "MANUAL",

                                sku:
                                    "",

                                barcode:
                                    "",

                                brand:
                                    "",

                                modelNumber:
                                    "",

                                availableStock:
                                    0,

                                reservedStock:
                                    0,
                            }
                            : {}),

                        itemName,
                    }
                );
            },
            [
                normalizedItems,
                updateItem,
            ]
        );

    const addEmptyRow =
        useCallback(() => {
            emitItems([
                ...normalizedItems,

                createEmptyInvoiceItem(
                    defaults
                ),
            ]);
        }, [
            defaults,
            emitItems,
            normalizedItems,
        ]);

    const removeRow =
        useCallback(
            (
                rowIndex
            ) => {
                const nextItems =
                    normalizedItems.filter(
                        (
                            _,
                            index
                        ) =>
                            index !==
                            rowIndex
                    );

                while (
                    nextItems.length <
                    minimumRows
                ) {
                    nextItems.push(
                        createEmptyInvoiceItem(
                            defaults
                        )
                    );
                }

                emitItems(
                    nextItems
                );
            },
            [
                defaults,
                emitItems,
                minimumRows,
                normalizedItems,
            ]
        );

    const duplicateRow =
        useCallback(
            (
                rowIndex
            ) => {
                const source =
                    normalizedItems[
                        rowIndex
                    ];

                const duplicated = {
                    ...source,

                    temporaryId:
                        createTemporaryId(),

                    metadata: {
                        ...source.metadata,
                    },
                };

                const nextItems = [
                    ...normalizedItems,
                ];

                nextItems.splice(
                    rowIndex + 1,
                    0,
                    duplicated
                );

                emitItems(
                    nextItems
                );
            },
            [
                emitItems,
                normalizedItems,
            ]
        );

    const moveRow =
        useCallback(
            (
                rowIndex,
                direction
            ) => {
                const targetIndex =
                    rowIndex +
                    direction;

                if (
                    targetIndex <
                        0 ||
                    targetIndex >=
                        normalizedItems.length
                ) {
                    return;
                }

                const nextItems = [
                    ...normalizedItems,
                ];

                const [
                    movedItem,
                ] =
                    nextItems.splice(
                        rowIndex,
                        1
                    );

                nextItems.splice(
                    targetIndex,
                    0,
                    movedItem
                );

                emitItems(
                    nextItems
                );
            },
            [
                emitItems,
                normalizedItems,
            ]
        );

    const handleDragStart =
        useCallback(
            (
                event,
                rowId
            ) => {
                if (disabled) {
                    event.preventDefault();
                    return;
                }

                setDraggedRowId(
                    rowId
                );

                event.dataTransfer.effectAllowed =
                    "move";

                event.dataTransfer.setData(
                    "text/plain",
                    rowId
                );
            },
            [disabled]
        );

    const handleDragOver =
        useCallback(
            (
                event,
                rowId
            ) => {
                if (
                    disabled ||
                    !draggedRowId ||
                    draggedRowId ===
                        rowId
                ) {
                    return;
                }

                event.preventDefault();
                event.dataTransfer.dropEffect =
                    "move";

                setDragOverRowId(
                    rowId
                );
            },
            [
                disabled,
                draggedRowId,
            ]
        );

    const handleDrop =
        useCallback(
            (
                event,
                targetRowId
            ) => {
                event.preventDefault();

                const sourceRowId =
                    draggedRowId ||
                    event.dataTransfer.getData(
                        "text/plain"
                    );

                if (
                    !sourceRowId ||
                    sourceRowId ===
                        targetRowId
                ) {
                    setDraggedRowId(
                        ""
                    );
                    setDragOverRowId(
                        ""
                    );
                    return;
                }

                const sourceIndex =
                    normalizedItems.findIndex(
                        (item) =>
                            item.temporaryId ===
                            sourceRowId
                    );

                const targetIndex =
                    normalizedItems.findIndex(
                        (item) =>
                            item.temporaryId ===
                            targetRowId
                    );

                if (
                    sourceIndex <
                        0 ||
                    targetIndex <
                        0
                ) {
                    setDraggedRowId(
                        ""
                    );
                    setDragOverRowId(
                        ""
                    );
                    return;
                }

                const nextItems = [
                    ...normalizedItems,
                ];

                const [movedItem] =
                    nextItems.splice(
                        sourceIndex,
                        1
                    );

                nextItems.splice(
                    targetIndex,
                    0,
                    movedItem
                );

                emitItems(
                    nextItems
                );

                setDraggedRowId(
                    ""
                );
                setDragOverRowId(
                    ""
                );
            },
            [
                draggedRowId,
                emitItems,
                normalizedItems,
            ]
        );

    const handleDragEnd =
        useCallback(() => {
            setDraggedRowId(
                ""
            );
            setDragOverRowId(
                ""
            );
        }, []);

    const changeQuantity =
        useCallback(
            (
                rowIndex,
                delta
            ) => {
                const current =
                    normalizedItems[
                        rowIndex
                    ];

                const nextQuantity =
                    Math.max(
                        roundMoney(
                            toNumber(
                                current.quantity
                            ) +
                                delta
                        ),
                        0
                    );

                updateItem(
                    rowIndex,
                    {
                        quantity:
                            nextQuantity,
                    }
                );
            },
            [
                normalizedItems,
                updateItem,
            ]
        );

    const formatMoney =
        useCallback(
            (
                amount
            ) =>
                `₹${roundMoney(
                    amount
                ).toLocaleString(
                    "en-IN",
                    {
                        minimumFractionDigits:
                            2,
                        maximumFractionDigits:
                            2,
                    }
                )}`,
            []
        );

    return (
        <section className="invoice-items-table-section invoice-items-compact-section">
            <div className="invoice-items-table-header">
                <div>
                    <h3>
                        Products / Items
                    </h3>

                    <p>
                        Select an inventory product or type a manual item. HSN/SAC is available for GST billing.
                    </p>
                </div>

                <button
                    type="button"
                    className="invoice-items-add-row-button"
                    disabled={disabled}
                    onClick={addEmptyRow}
                >
                    <FiPlus />
                    Add Row
                </button>
            </div>

            <div className="invoice-items-table-scroll">
                <table className="invoice-items-table invoice-items-quotation-layout">
                    <colgroup>
                        <col className="invoice-col-drag" />
                        <col className="invoice-col-item" />
                        <col className="invoice-col-description" />
                        <col className="invoice-col-hsn" />
                        <col className="invoice-col-qty" />
                        <col className="invoice-col-unit" />
                        {showPriceMode && <col className="invoice-col-price-mode" />}
                        <col className="invoice-col-tax" />
                        <col className="invoice-col-price" />
                        <col className="invoice-col-discount" />
                        <col className="invoice-col-amount" />
                        <col className="invoice-col-action" />
                    </colgroup>

                    <thead>
                        <tr>
                            <th aria-label="Row order" />
                            <th>Item</th>
                            <th>Description</th>
                            <th>HSN/SAC</th>
                            <th>Qty</th>
                            <th>Unit</th>
                            {showPriceMode && <th>Price Mode</th>}
                            <th>Tax</th>
                            <th>Price/Unit</th>
                            <th>Discount</th>
                            <th>Amount</th>
                            <th>Action</th>
                        </tr>
                    </thead>

                    <tbody>
                        {calculatedItems.map((item, rowIndex) => (
                            <tr
                                key={item.temporaryId}
                                className={[
                                    "invoice-items-row",
                                    Object.keys(
                                        rowErrors[
                                            rowIndex
                                        ] ||
                                        {}
                                    ).length
                                        ? "invoice-items-row-error"
                                        : "",
                                    draggedRowId ===
                                    item.temporaryId
                                        ? "invoice-items-row-dragging"
                                        : "",
                                    dragOverRowId ===
                                    item.temporaryId
                                        ? "invoice-items-row-drag-over"
                                        : "",
                                ]
                                    .filter(Boolean)
                                    .join(" ")}
                                onDragOver={(event) =>
                                    handleDragOver(
                                        event,
                                        item.temporaryId
                                    )
                                }
                                onDrop={(event) =>
                                    handleDrop(
                                        event,
                                        item.temporaryId
                                    )
                                }
                            >
                                <td className="invoice-items-drag-cell">
                                    <span
                                        className="invoice-items-drag-handle"
                                        title="Drag to reorder row"
                                        draggable={!disabled}
                                        role="button"
                                        tabIndex={disabled ? -1 : 0}
                                        onDragStart={(event) =>
                                            handleDragStart(
                                                event,
                                                item.temporaryId
                                            )
                                        }
                                        onDragEnd={
                                            handleDragEnd
                                        }
                                    >
                                        <FiMenu />
                                    </span>
                                </td>

                                <td className="invoice-items-product-table-cell">
                                    <ProductAutocompleteCell
                                        item={item}
                                        rowIndex={rowIndex}
                                        disabled={disabled}
                                        defaults={defaults}
                                        allowManual={allowManual}
                                        onSelect={handleSelectProduct}
                                        onManual={handleManualItem}
                                    />

                                    {(getVisibleError(rowIndex, "itemName") ||
                                        getVisibleError(rowIndex, "stock")) && (
                                        <small className="invoice-items-field-error">
                                            {getVisibleError(rowIndex, "itemName") ||
                                                getVisibleError(rowIndex, "stock")}
                                        </small>
                                    )}
                                </td>

                                <td>
                                    <input
                                        type="text"
                                        className="invoice-items-input"
                                        value={item.description}
                                        disabled={disabled}
                                        placeholder="Description"
                                        onChange={(event) =>
                                            updateItem(rowIndex, {
                                                description: event.target.value,
                                            })
                                        }
                                    />
                                </td>

                                <td>
                                    <input
                                        type="text"
                                        className="invoice-items-input invoice-items-hsn-input"
                                        value={item.hsnSacCode}
                                        disabled={disabled}
                                        placeholder="HSN"
                                        onChange={(event) =>
                                            updateItem(rowIndex, {
                                                hsnSacCode: event.target.value.toUpperCase(),
                                            })
                                        }
                                    />
                                </td>

                                <td>
                                    <input
                                        type="number"
                                        className="invoice-items-input invoice-items-qty-input"
                                        min="0.01"
                                        step="0.01"
                                        value={item.quantity}
                                        disabled={disabled}
                                        onBlur={() => markTouched(rowIndex, "quantity")}
                                        onChange={(event) =>
                                            updateItem(rowIndex, {
                                                quantity: event.target.value,
                                            })
                                        }
                                    />
                                    {getVisibleError(rowIndex, "quantity") && (
                                        <small className="invoice-items-field-error">
                                            {getVisibleError(rowIndex, "quantity")}
                                        </small>
                                    )}
                                </td>

                                <td>
                                    <select
                                        className="invoice-items-select"
                                        value={item.unit}
                                        disabled={disabled}
                                        onChange={(event) =>
                                            updateItem(rowIndex, {
                                                unit: event.target.value,
                                            })
                                        }
                                    >
                                        {UNIT_OPTIONS.map((unit) => (
                                            <option key={unit} value={unit}>
                                                {unit.toUpperCase()}
                                            </option>
                                        ))}
                                    </select>
                                </td>

                                {showPriceMode && (
                                    <td>
                                        <select
                                            className="invoice-items-select"
                                            value={item.priceMode}
                                            disabled={disabled}
                                            onChange={(event) =>
                                                updateItem(rowIndex, {
                                                    priceMode: event.target.value,
                                                })
                                            }
                                        >
                                            <option value="TAX_EXCLUSIVE">Without Tax</option>
                                            <option value="TAX_INCLUSIVE">With Tax</option>
                                        </select>
                                    </td>
                                )}

                                <td>
                                    <div className="invoice-items-paired-field">
                                        <select
                                            className="invoice-items-select"
                                            value={item.taxPercentage}
                                            disabled={disabled}
                                            onChange={(event) =>
                                                updateItem(rowIndex, {
                                                    taxPercentage: event.target.value,
                                                })
                                            }
                                        >
                                            {TAX_OPTIONS.map((rate) => (
                                                <option key={rate} value={rate}>
                                                    {rate === 0
                                                        ? "NONE"
                                                        : `${isInterState ? "IGST" : "GST"} @ ${rate}%`}
                                                </option>
                                            ))}
                                        </select>

                                        <div className="invoice-items-paired-result">
                                            <span>Tax</span>
                                            <strong>{formatMoney(item.calculation.taxAmount)}</strong>
                                        </div>
                                    </div>
                                </td>

                                <td>
                                    <input
                                        type="number"
                                        className="invoice-items-input"
                                        min="0"
                                        step="0.01"
                                        value={item.rate}
                                        disabled={disabled}
                                        onBlur={() => markTouched(rowIndex, "rate")}
                                        onChange={(event) =>
                                            updateItem(rowIndex, {
                                                rate: event.target.value,
                                            })
                                        }
                                    />
                                </td>

                                <td>
                                    <div className="invoice-items-discount-pair">
                                        <select
                                            className="invoice-items-select"
                                            value={item.discountType}
                                            disabled={disabled}
                                            onChange={(event) =>
                                                updateItem(rowIndex, {
                                                    discountType: event.target.value,
                                                })
                                            }
                                        >
                                            {DISCOUNT_TYPE_OPTIONS.map((option) => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </select>

                                        <input
                                            type="number"
                                            className="invoice-items-input"
                                            min="0"
                                            step="0.01"
                                            value={item.discountValue}
                                            disabled={disabled || item.discountType === "NONE"}
                                            onChange={(event) =>
                                                updateItem(rowIndex, {
                                                    discountValue: event.target.value,
                                                })
                                            }
                                        />

                                        <div className="invoice-items-discount-result">
                                            <span>Discount</span>
                                            <strong>{formatMoney(item.calculation.discountAmount)}</strong>
                                        </div>
                                    </div>
                                </td>

                                <td>
                                    <div className="invoice-items-line-total">
                                        <strong>{formatMoney(item.calculation.totalAmount)}</strong>
                                        {item.priceMode === "TAX_INCLUSIVE" && <span>Tax included</span>}
                                    </div>
                                </td>

                                <td>
                                    <div className="invoice-items-row-actions invoice-items-row-actions-compact">
                                        <button
                                            type="button"
                                            title="Duplicate item"
                                            disabled={disabled}
                                            onClick={() => duplicateRow(rowIndex)}
                                        >
                                            <FiCopy />
                                        </button>
                                        <button
                                            type="button"
                                            title="Delete item"
                                            className="invoice-items-delete-button"
                                            disabled={disabled}
                                            onClick={() => removeRow(rowIndex)}
                                        >
                                            <FiTrash2 />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>

                    <tfoot>
                        <tr>
                            <td colSpan={4}>
                                <button
                                    type="button"
                                    className="invoice-items-add-row-link"
                                    disabled={disabled}
                                    onClick={addEmptyRow}
                                >
                                    <FiPlus />
                                    Add Row
                                </button>
                            </td>
                            <td>
                                <strong>
                                    {calculatedItems.reduce(
                                        (sum, current) => sum + toNumber(current.quantity),
                                        0
                                    )}
                                </strong>
                            </td>
                            <td colSpan={(showPriceMode ? 4 : 3)}>
                                <strong>Total Items: {calculatedItems.length}</strong>
                            </td>
                            <td>
                                <strong>Discount: {formatMoney(totals.discountAmount)}</strong>
                            </td>
                            <td>
                                <strong>{formatMoney(totals.totalAmount)}</strong>
                            </td>
                            <td />
                        </tr>
                    </tfoot>
                </table>
            </div>
        </section>
    );
};

export {
    UNIT_OPTIONS,
    TAX_OPTIONS,
    createEmptyInvoiceItem,
    normalizeInvoiceItem,
    calculateInvoiceItem,
    validateInvoiceItem,
    mapProductToInvoiceItem,
};

export default InvoiceItemsTable;